import { Box, Typography, useTheme } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import React, { ReactNode } from 'react';

import {
  getPageHeaderIconComponent,
  type PageHeaderIconType,
  type PageHeaderIconVariant,
} from './pageHeaderIcons';

function getIconTintSource(
  theme: Theme,
  iconVariant: PageHeaderIconVariant,
): string {
  switch (iconVariant) {
    case 'secondary':
      return theme.palette.secondary.main;
    case 'error':
      return theme.palette.error.main;
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'info':
      return theme.palette.info.main;
    case 'inherit':
      return theme.palette.text.primary;
    case 'outlined':
    case 'primary':
    default:
      return theme.palette.primary.main;
  }
}

const ICON_SIZE = 36;
const ICON_FONT_SIZE = 'small';

export type PageHeaderTitleVariant = 'default' | 'large' | 'small';

export type PageHeaderIconBackground = 'tint' | 'transparent';

export type PageHeaderIconColorProps =
  | {
      iconVariant?: PageHeaderIconVariant;
      explicitColor?: never;
      iconBackground?: never;
    }
  | {
      explicitColor: string;
      iconVariant?: never;
      iconBackground?: PageHeaderIconBackground;
    };

const TITLE_VARIANT_STYLES: Record<
  PageHeaderTitleVariant,
  React.CSSProperties
> = {
  default: { fontSize: '1rem', fontWeight: 500 },
  large: { fontSize: '1.25rem', fontWeight: 600 },
  small: { fontSize: '0.875rem', fontWeight: 400 },
};

export interface PageHeaderTitleSectionBaseProps {
  iconType?: PageHeaderIconType;
  icon?: ReactNode;
  title?: string;
  subtitle?: string;
  titleVariant?: PageHeaderTitleVariant;
  titleStyle?: React.CSSProperties;
  children?: ReactNode;
  noExpand?: boolean;
}

export type PageHeaderTitleSectionProps = PageHeaderTitleSectionBaseProps &
  PageHeaderIconColorProps;

const PageHeaderTitleSection: React.FC<PageHeaderTitleSectionProps> = (
  props,
) => {
  const {
    iconType,
    icon,
    title,
    subtitle,
    titleVariant = 'default',
    titleStyle,
    children,
    noExpand = false,
  } = props;

  const theme = useTheme();

  const explicitColor =
    'explicitColor' in props &&
    props.explicitColor !== undefined &&
    props.explicitColor !== ''
      ? props.explicitColor
      : undefined;

  const iconVariant: PageHeaderIconVariant =
    explicitColor !== undefined ? 'primary' : (props.iconVariant ?? 'primary');

  const explicitIconBackground: PageHeaderIconBackground | undefined =
    explicitColor !== undefined
      ? ((
          props as Extract<
            PageHeaderTitleSectionProps,
            { explicitColor: string }
          >
        ).iconBackground ?? 'tint')
      : undefined;

  const isOutlined =
    explicitColor !== undefined
      ? explicitIconBackground === 'transparent'
      : iconVariant === 'outlined';

  const resolvedIcon: ReactNode =
    icon ??
    (iconType != null
      ? (() => {
          const IconComponent = getPageHeaderIconComponent(iconType);
          if (explicitColor !== undefined) {
            return (
              <IconComponent
                fontSize={ICON_FONT_SIZE}
                sx={{ color: explicitColor }}
              />
            );
          }
          return (
            <IconComponent
              fontSize={ICON_FONT_SIZE}
              color={
                isOutlined || iconVariant === 'outlined'
                  ? 'primary'
                  : (iconVariant as Exclude<PageHeaderIconVariant, 'outlined'>)
              }
            />
          );
        })()
      : null);

  const getIconBoxSx = () => {
    if (isOutlined) {
      return {
        backgroundColor: 'transparent',
      };
    }
    const tintSource =
      explicitColor !== undefined
        ? explicitColor
        : getIconTintSource(theme, iconVariant);
    return {
      backgroundColor: alpha(tintSource, 0.12),
    };
  };

  const hasCustomContent = children != null;
  const hasTitleContent = title != null && title !== '';

  if (!hasCustomContent && !hasTitleContent) {
    return null;
  }

  if (hasCustomContent) {
    return (
      <Box
        sx={{
          flex: noExpand ? 'none' : 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 40,
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: noExpand ? 'none' : 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1.25,
        minHeight: ICON_SIZE,
      }}
    >
      {resolvedIcon != null && (
        <Box
          sx={{
            width: ICON_SIZE,
            height: ICON_SIZE,
            flexShrink: 0,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...getIconBoxSx(),
          }}
        >
          {resolvedIcon}
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: 0.25,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: 'text.primary',
            lineHeight: 1.2,
            margin: 0,
            ...TITLE_VARIANT_STYLES[titleVariant],
            ...titleStyle,
          }}
        >
          {title}
        </Typography>
        {subtitle != null && subtitle !== '' && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.8125rem',
              lineHeight: 1.2,
              margin: 0,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PageHeaderTitleSection;
